import type { Environment, Project } from '@hangar/shared';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { RowActions, type RowAction } from '@/components/RowActions';
import { EnvPill } from '@/components/badges';
import { EnvironmentForm } from './EnvironmentForm';
import { Forks, Patterns, ProtectionChip } from './EnvironmentCells';
import type { EditingTarget } from './environmentsCardModel';

export interface EnvironmentListProps {
  project: Project;
  live: Environment[];
  editing: EditingTarget;
  setEditing: (target: EditingTarget) => void;
  actionsFor: (env: Environment) => RowAction[];
}

/** Phone layout: one card per environment, the editor opening inside it. */
export function EnvironmentsPhoneList(props: EnvironmentListProps) {
  return (
    <ul className="flex flex-col gap-3 p-4" aria-label="Environments">
      {props.live.map((env, i) => (
        <EnvironmentCard key={env.id} env={env} index={i} {...props} />
      ))}
    </ul>
  );
}

function EnvironmentCard({ env, index, project, editing, setEditing, actionsFor }: EnvironmentListProps & { env: Environment; index: number }) {
  const isEditing = editing === env.id;
  return (
    <li data-testid={`env-row-${env.slug}`} style={stagger(index)} className={cn('enter overflow-hidden rounded-lg border bg-card dark:bg-background', isEditing && 'border-border-strong')}>
      <div className="flex items-center gap-2 p-3">
        <EnvPill env={{ ...env, name: env.slug }} />
        <ProtectionChip env={env} />
        {isEditing ? <span className="ml-auto text-xs text-subtle">Editing…</span> : <RowActions actions={actionsFor(env)} subject={env.slug} testId={`env-actions-${env.slug}`} className="-my-1 ml-auto" />}
      </div>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 border-t px-3 py-2.5 text-xs">
        <dt className="text-subtle">Allowed branches</dt>
        <dd className="min-w-0">
          <Patterns env={env} />
        </dd>
        <dt className="text-subtle">Forks · untrusted</dt>
        <dd>
          <Forks env={env} />
        </dd>
      </dl>
      {isEditing ? (
        <div className="border-t">
          <EnvironmentForm key={env.id} project={project} environment={env} onDone={() => setEditing(null)} />
        </div>
      ) : null}
    </li>
  );
}
